import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gift, Lock, CheckCircle2, Clock, Loader2, ShieldQuestion, ArrowUpCircle, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { RankBadge } from '../components/RankBadge';
import { getMyRank, claimReward } from '../services/api';
import { toast } from '../hooks/use-toast';

const RanksPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getMyRank();
      setData(res);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load your ranks', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openClaim = (milestone) => {
    setClaimTarget(milestone);
    setSelectedOption('');
    setNote('');
  };

  const submitClaim = async () => {
    if (!claimTarget) return;
    if (claimTarget.rewardOptions?.length > 0 && !selectedOption) {
      toast({ title: 'Pick an option', description: 'Please choose one of the reward options.', variant: 'destructive' });
      return;
    }
    try {
      setClaiming(true);
      const res = await claimReward(claimTarget.id, note, selectedOption);
      toast({ title: 'Reward Claimed!', description: res.message });
      setClaimTarget(null);
      await load();
    } catch (error) {
      toast({
        title: 'Could not claim',
        description: error.response?.data?.detail || 'Something went wrong',
        variant: 'destructive'
      });
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const memberSince = data?.memberSince ? new Date(data.memberSince).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : null;
  const monthsIn = data ? Math.floor(data.tenureDays / 30.44) : 0;

  return (
    <div className="min-h-screen bg-black text-amber-50" data-testid="ranks-page">
      {/* Film grain */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04] z-40 mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")" }} />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <Link to="/feed" className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-headline tracking-wider text-sm mb-6" data-testid="ranks-back-link">
          <ArrowLeft className="w-4 h-4" /> BACK TO HQ
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-700" />
          <span className="text-amber-700 font-typewriter text-xs tracking-[0.3em]">THE BUREAU · SERVICE RECORD</span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-700" />
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline tracking-tight text-red-500 mb-3">YOUR SERVICE RECORD</h1>
        <p className="text-amber-200/70 font-typewriter max-w-2xl">
          Every month of service earns your stripes. Climb the ranks of the Bureau, earn your badge, and unlock rewards for your loyalty.
        </p>
      </div>

      {/* Not a paid member */}
      {!data?.isPaid && (
        <div className="max-w-6xl mx-auto px-4 mb-10">
          <Card className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-700/50">
            <CardContent className="p-8 text-center">
              <ShieldQuestion className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-headline text-amber-400 tracking-wider mb-2">JOIN THE BUREAU TO EARN YOUR BADGE</h2>
              <p className="text-amber-200/70 font-typewriter mb-6 max-w-lg mx-auto">
                Ranks and rewards are earned through paid membership. Subscribe to start your service clock and begin climbing the ranks.
              </p>
              <Link to="/tiers">
                <Button className="bg-amber-600 hover:bg-amber-500 text-black font-headline tracking-wider" data-testid="ranks-subscribe-cta">
                  VIEW MEMBERSHIP TIERS
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Current rank card (paid) */}
      {data?.isPaid && data.currentRank && (
        <div className="max-w-6xl mx-auto px-4 mb-12">
          <Card className="bg-gradient-to-br from-gray-900 via-gray-950 to-black border-2 border-amber-900/50 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-3">
                <div className="lg:col-span-1 p-8 bg-gradient-to-br from-amber-900/10 to-transparent border-b-2 lg:border-b-0 lg:border-r-2 border-amber-900/30 flex flex-col items-center justify-center">
                  <span className="font-typewriter text-amber-600/80 text-xs tracking-widest mb-4">CURRENT RANK</span>
                  <RankBadge rank={data.currentRank} size="xl" />
                  <h2 className="text-3xl font-headline tracking-wider mt-4 text-amber-300">{data.currentRank.rankName.toUpperCase()}</h2>
                  {data.currentRank.isOG && (
                    <Badge className="mt-2 bg-yellow-700/40 text-yellow-300 border border-yellow-500/50 font-headline tracking-widest">OG STATUS</Badge>
                  )}
                  {memberSince && (
                    <p className="text-gray-500 font-typewriter text-xs mt-3">Member since {memberSince} · {monthsIn} mo</p>
                  )}
                </div>

                <div className="lg:col-span-2 p-8 flex flex-col justify-center">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="font-typewriter text-amber-600/80 text-xs tracking-widest">TENURE</span>
                    <span className="font-headline text-amber-400 text-2xl">{data.tenureDays} days</span>
                  </div>
                  {data.nextRank ? (
                    <>
                      <div className="h-3 bg-gray-800 border border-amber-900/40 rounded-sm overflow-hidden mb-1">
                        <div className="h-full bg-gradient-to-r from-amber-700 via-amber-500 to-red-500 transition-all duration-700" style={{ width: `${data.progressPct}%` }} />
                      </div>
                      <div className="flex justify-between text-[10px] font-typewriter text-gray-500 mb-4">
                        <span>{data.currentRank.rankName}</span>
                        <span>{data.nextRank.rankName}</span>
                      </div>
                      <p className="text-amber-200/70 font-typewriter text-sm">
                        <span className="text-amber-400 font-bold">{data.daysToNext} days</span> until{' '}
                        <span className="text-amber-300 font-headline tracking-wider">{data.nextRank.rankName}</span>
                      </p>
                    </>
                  ) : (
                    <p className="text-red-400 font-typewriter">Highest rank achieved. You ARE the Bureau.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* The Ladder */}
      <div className="max-w-6xl mx-auto px-4 mb-16">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-2xl font-headline tracking-widest text-amber-400">THE LADDER</h2>
          <div className="h-px flex-1 bg-amber-900/40" />
        </div>

        <div className="space-y-3">
          {(data?.milestones || []).map((m) => {
            const reached = m.reached;
            const isCurrent = data?.currentRank && data.currentRank.id === m.id;
            return (
              <div
                key={m.id}
                className={`relative p-4 sm:p-5 rounded-lg border-2 transition-all ${
                  isCurrent ? 'border-amber-500 bg-amber-950/30 shadow-lg shadow-amber-500/20'
                  : reached ? 'border-amber-900/50 bg-gray-900/60'
                  : 'border-gray-800 bg-gray-950/40 opacity-70'
                }`}
                data-testid={`milestone-row-${m.months}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <RankBadge rank={m} size="md" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-headline tracking-widest text-lg text-amber-200">{m.rankName.toUpperCase()}</h3>
                      <span className="text-[11px] font-typewriter text-gray-500">
                        {m.months === 0 ? 'Starting rank' : `${m.months} months`}
                      </span>
                      {m.isOG && <Badge className="bg-yellow-700/30 text-yellow-300 border border-yellow-600/40 text-[9px] font-headline tracking-widest">OG</Badge>}
                      {isCurrent && <Badge className="bg-amber-500 text-black text-[9px] font-headline tracking-widest">YOU</Badge>}
                    </div>
                    <p className="mt-0.5 font-typewriter text-xs text-gray-500 italic">{m.flavor}</p>
                    {m.rewardTitle ? (
                      <>
                        <p className="mt-1.5 font-typewriter text-sm text-amber-400 flex items-center gap-1.5">
                          <Gift className="w-3.5 h-3.5" /> {m.rewardTitle}
                        </p>
                        {m.rewardDesc && <p className="mt-0.5 font-typewriter text-xs text-gray-400">{m.rewardDesc}</p>}
                      </>
                    ) : m.months > 0 ? (
                      <p className="mt-1.5 font-typewriter text-xs text-gray-600">No reward at this rank for your tier.</p>
                    ) : null}
                    {m.upsell && (
                      <p className="mt-1 font-typewriter text-[11px] text-amber-600/80 flex items-center gap-1" data-testid={`upsell-${m.months}`}>
                        <ArrowUpCircle className="w-3 h-3" /> {m.upsell}
                      </p>
                    )}
                  </div>

                  {/* Action / status */}
                  <div className="flex-shrink-0 self-center">
                    {m.claimable ? (
                      <Button
                        size="sm"
                        onClick={() => openClaim(m)}
                        className="bg-amber-600 hover:bg-amber-500 text-black font-headline tracking-wider"
                        data-testid={`claim-reward-${m.months}`}
                      >
                        <Gift className="w-4 h-4 mr-1" /> Claim
                      </Button>
                    ) : m.claimed ? (
                      <span className="inline-flex items-center gap-1 text-green-500 font-headline text-xs tracking-wider" data-testid={`claimed-${m.months}`}>
                        <CheckCircle2 className="w-4 h-4" /> {m.claimStatus === 'fulfilled' ? 'FULFILLED' : 'CLAIMED'}
                      </span>
                    ) : reached && !m.hasReward ? (
                      <span className="inline-flex items-center gap-1 text-amber-600/70 font-headline text-xs tracking-wider">
                        <CheckCircle2 className="w-4 h-4" /> UNLOCKED
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-600 font-headline text-xs tracking-wider">
                        <Lock className="w-4 h-4" /> LOCKED
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How it works */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="bg-gray-900/40 border border-amber-900/30 rounded-lg p-5 flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="font-typewriter text-sm text-gray-400">
            Ranks are earned by your <span className="text-amber-400">time as a paid member</span> — your service clock started the day you joined.
            Reach a milestone, hit <span className="text-amber-400">Claim</span>, and Daria takes care of the rest.
            Note: your service resets if your membership fully lapses.
          </p>
        </div>
      </div>
      {/* Claim dialog */}
      <Dialog open={!!claimTarget} onOpenChange={(o) => !o && setClaimTarget(null)}>
        <DialogContent className="bg-gray-900 border-2 border-amber-900/40 sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-headline text-xl text-amber-500 tracking-wider flex items-center gap-2">
              <Gift className="w-5 h-5" /> CLAIM REWARD
            </DialogTitle>
            <DialogDescription className="font-typewriter text-gray-400">
              {claimTarget?.rankName} · {claimTarget?.rewardTitle}
            </DialogDescription>
          </DialogHeader>

          {claimTarget && (
            <div className="space-y-4 py-1">
              {claimTarget.rewardDesc && (
                <p className="font-typewriter text-sm text-gray-300">{claimTarget.rewardDesc}</p>
              )}

              {claimTarget.rewardOptions?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-amber-500 font-headline tracking-wider text-xs">CHOOSE YOUR REWARD</p>
                  {claimTarget.rewardOptions.map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedOption(opt)}
                      data-testid={`reward-option-${i}`}
                      className={`w-full text-left p-3 rounded-lg border transition-colors font-typewriter text-sm ${selectedOption === opt ? 'border-amber-500 bg-amber-900/20 text-amber-200' : 'border-gray-700 bg-gray-800/50 text-gray-300 hover:border-amber-900/50'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              <div className="space-y-1">
                <p className="text-amber-500 font-headline tracking-wider text-xs">ANYTHING DARIA SHOULD KNOW? (OPTIONAL)</p>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. which films you'd like, a photoset theme, your Q&A questions, or 'please don't mail me'"
                  className="bg-gray-800 border-amber-900/40 focus:border-amber-600 font-typewriter"
                  data-testid="claim-note-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimTarget(null)} className="border-gray-600 text-gray-400 hover:bg-gray-800 font-headline tracking-wider" data-testid="claim-cancel-btn">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={submitClaim} disabled={claiming} className="bg-amber-600 hover:bg-amber-500 text-black font-headline tracking-wider" data-testid="claim-confirm-btn">
              {claiming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />} Claim Reward
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RanksPage;
